import * as _ from "lodash";
import * as React from "react";
import * as Comp from "../../../../components";
import { AbstractListItem } from "../../../../models/abstract";
import { ElementPosition } from "../../../../models/generic";
import { PopupMenuItem } from "../../../../models/popup";
import { SearchFilter } from "../../../../models/search";
import { Api } from "../../../../services/Api";
import { Utility } from "../../../../utils";

interface OwnProps {
    addNoneSelection?: string;
    api: Api;
    confirmationText?: string;
    promptConfirmation?: boolean;
    onError?: (error: string) => void;
    onProcessing?: () => void;
    onUpdated?: (updatedAbstractIds: number[], selectedId?: number) => void;
}

interface OwnState {
    // abstractId?: number;
    items: PopupMenuItem[];
    id: string;
    pos: ElementPosition;
    processing: boolean;
    selectedId?: number;
    show: boolean;
}

export interface UpdatePopupResponse {
    abstractId: number;
    selectedId?: number;
}

export abstract class UpdatePopup<TProps> extends React.Component<TProps & OwnProps, OwnState> {

    private abstractId: number | undefined;
    private confirmModal: Comp.Modal.ConfirmModal | null = null;

    public constructor(props: any) {
        super(props);

        this.state = {
            id: `abstract-update-popup-${Utility.guid()}`,
            items: [],
            pos: { top: 0, left: 0 },
            processing: false,
            show: false,
        };

        this.onItemClicked = this.onItemClicked.bind(this);
        this.handlePopupClickOutside = this.handlePopupClickOutside.bind(this);
    }

    public componentDidMount() {
        if (typeof document !== "undefined")
            document.addEventListener("mousedown", this.handlePopupClickOutside);
    }

    public componentWillUnmount() {
        if (typeof document !== "undefined")
            document.removeEventListener("mousedown", this.handlePopupClickOutside);
    }

    public abstract doUpdatePromise(abstractId?: number, selectedId?: number): Promise<UpdatePopupResponse>;

    public onItemClicked(id?: number, confirmed?: boolean) {

        const items = [...this.state.items];
        items.map((item) => {
            item.selecting = item.id === id;
        });

        const selectedItem = _.find(items, (x) => x.selecting);

        if (!confirmed && this.props.promptConfirmation && this.confirmModal) {
            this.confirmModal.show(selectedItem);
            return;
        }

        this.setState({ ...this.state, items, processing: true }, () => {
            if (this.props.onProcessing)
                this.props.onProcessing();

            this.doUpdatePromise(this.abstractId, id).then((result) => {
                // update success
                this.onUpdateComplete(result);
            }, (err) => {
                // update failed
                this.onUpdateError(err);
            });
        });
    }

    /**
     * Callback method called after update promise method has successfully returned
     * @param result The result of the promise call
     */
    public onUpdateComplete(result: UpdatePopupResponse) {
        const items = [...this.state.items];
        items.map((item) => {
            item.selecting = false;
            item.checked = item.id === result.selectedId;
        });

        this.setState({
            ...this.state,
            items,
        }, () => {
            setTimeout(() => {
                this.close();

                if (this.props.onUpdated)
                    this.props.onUpdated([result.abstractId], result.selectedId);
            }, 250);
        });
    }

    public onUpdateError(error: string) {
        const items = [...this.state.items];
        items.map((item) => {
            item.selecting = false;
        });

        this.setState({
            ...this.state,
            items,
            processing: false,
        });

        if (this.props.onError)
            this.props.onError(error);
    }

    public close(callback?: () => void) {
        this.abstractId = undefined;
        this.setState({
            ...this.state,
            // abstractId: undefined,
            processing: false,
            selectedId: undefined,
            show: false,
        }, callback);
    }

    public open(filterItems: SearchFilter[], pos: ElementPosition, abstract: AbstractListItem, selectedId?: number) {
        let items: PopupMenuItem[] = [];
        this.abstractId = abstract.id;

        items = _.sortBy(filterItems, ["name"]).map((item) => {
            return {
                checked: item.id === selectedId,
                id: item.id,
                name: item.name,
                selecting: false,
            };
        });

        // add none-selection if set
        if (this.props.addNoneSelection)
            items = [{
                name: this.props.addNoneSelection as string,
                checked: !selectedId,
            }, ...items];

        this.setState({
            ...this.state,
            // abstractId: abstract.id,
            items,
            selectedId,
            show: false,
        }, () => {
            setTimeout(() => this.setState({
                ...this.state,
                // abstractId: abstract.id,
                pos,
                show: true,
            }), 100);
        });
    }

    public render() {

        return <Comp.Generic.WrapperLess>
            {this.props.promptConfirmation
                ? <Comp.Modal.ConfirmModal
                    headerClassName="warning"
                    onCancel={() => this.close()}
                    onProceed={() => this.handleConfirm()}
                    title="Warning"
                    renderBody={(data?: PopupMenuItem) => {
                        const text = ((this.props.confirmationText as string) || "Are you sure?")
                            .replace(/\{item\}/g, data ? data.name : "None");
                        return <div dangerouslySetInnerHTML={{ __html: text}} />;
                    }}
                    ref={(ref) => this.confirmModal = ref}
                />
                : null}

            <Comp.Generic.PopupMenu
                className="updatable-popup"
                id={this.state.id}
                items={this.state.items}
                onItemClicked={this.onItemClicked}
                pos={this.state.pos}
                processing={this.state.processing}
                show={this.state.show}
            />
        </Comp.Generic.WrapperLess>;
    }

    private async handleConfirm() {
        setTimeout(async () => {
            if (this.confirmModal)
                await this.confirmModal.close();
        }, 10);

        setTimeout(async () => {
            const selectedItem = _.find(this.state.items, (x) => x.selecting);
            const itemId = selectedItem ? selectedItem.id : undefined;
            this.onItemClicked(itemId, true);
        }, 20);
    }

    private handlePopupClickOutside(e: MouseEvent) {
        const paths = ((e as any).path || (e.composedPath && e.composedPath())) as HTMLElement[];
        let updatableClicked = false;

        if (paths && Array.isArray(paths)) {
            for (const p of paths) {
                // detect if click was outside popup.
                // exception if confirmation is enabled and click occurred in modal dialog.
                if (p.classList !== undefined && (p.id === this.state.id || (this.props.promptConfirmation && p.classList.contains("modal")))) {
                    updatableClicked = true;
                    break;
                }
            }
        }
        else if (paths === undefined) {
            // most likely IE; use the target/parentNode to determine outside click
            let n = e.target as HTMLElement;
            while (n.parentElement) {
                // do stuff
                if (n.className && (n.id === this.state.id || (this.props.promptConfirmation && n.className.indexOf("modal") > -1))) {
                    updatableClicked = true;
                    break;
                }

                n = n.parentElement;
            }
        }

        if (!updatableClicked) {
            this.close();
        }
    }
}



// WEBPACK FOOTER //
// ./src/containers/adminAbstracts/list/components/UpdatePopup.tsx